// Taps on push notifications, read on the web side (push fix 1).
//
// A tap reaches the app in one of three ways:
// - the mobile app's bridge event `push_notification_opened`, sent once the app has received this page's
//   `init_app` (message: JSON `{notificationId, notificationType, redirectTo, appState, platformOS, deliveryId}`;
//   `deliveryId` only names the message, for the page's confirmation, and is not part of the tap);
// - the desktop service worker (public/firebase-messaging-sw.js) posting a message to a Jungle window that is
//   already open and answers that it can open the push (`answerServiceWorkerPushNotificationOpenedMessage`);
// - with no Jungle window open, a new window at the push's url, which the service worker gives the
//   `push_notification_id` and `push_notification_type` parameters.
// Each is reported as "Push Notification Opened" with the camelCase properties of the senders' "Push
// Notification Sent" (`notificationId` joins the two) and the app opens the screen the push is about:
// `redirectTo`, an app path without its leading slash that may carry a query ("" means where the app opens).
//
// Small and dependency-free on purpose: App.tsx imports it at startup (scripts/checkStartupBudget.mjs).

export const PUSH_NOTIFICATION_OPENED_EVENT = "Push Notification Opened";
// Must match the `type` the service worker posts.
export const PUSH_NOTIFICATION_OPENED_SERVICE_WORKER_MESSAGE_TYPE = "jungle_push_notification_opened";
// Must match the service worker: the two messages on the port that comes with its tap message. The window
// answers that it can open the push; the service worker tells the one window it picks to open it.
export const PUSH_NOTIFICATION_RECEIVED_BY_WINDOW_MESSAGE_TYPE = "jungle_push_notification_received";
export const PUSH_NOTIFICATION_OPEN_IN_THIS_WINDOW_MESSAGE_TYPE = "jungle_push_notification_open_here";
// Must match the parameters the service worker adds to the address of a window it opens.
export const PUSH_NOTIFICATION_ID_URL_PARAMETER = "push_notification_id";
export const PUSH_NOTIFICATION_TYPE_URL_PARAMETER = "push_notification_type";
// The service worker's own `utm_source=push_notification_<type>` (read by AppRouter for "Desktop Notification
// opened"); it is not part of the screen the push is about.
const PUSH_NOTIFICATION_UTM_SOURCE_PREFIX = "push_notification_";
// Home's daily review start parameter (TodaysReviewComponent), which review reminders and streak warnings
// carry: `?startReview=true`.
const START_TODAYS_REVIEW_URL_PARAMETER = "startReview";

export type OpenedPushNotification = {
  notificationId: string | null;
  notificationType: string | null;
  redirectTo: string | null;
  // Mobile: "cold_start", "background" or "foreground". Desktop: "open_window" or "new_window".
  appState: string | null;
  // Mobile: "ios" or "android". Desktop: "web".
  platformOS: string | null;
  channel: "mobile" | "desktop";
};

export type PushNotificationDestination =
  { kind: "stay" } | { kind: "start_todays_review" } | { kind: "open_in_app_path"; inAppPath: string };

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readStringOrNull(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

// The mobile app's message is a JSON string; one the bridge already parsed is read the same way, and anything
// unreadable has no fields.
function readMobilePushNotificationOpenedMessageFields(message: unknown): Record<string, unknown> {
  let messageFields: unknown = message;
  if (typeof message === "string") {
    try {
      messageFields = JSON.parse(message);
    } catch {
      messageFields = null;
    }
  }
  return isPlainObject(messageFields) ? messageFields : {};
}

/**
 * The tap in the mobile app's `push_notification_opened` message, without its `deliveryId`. A message that cannot
 * be read still counts as a tap: every field is then null.
 */
export function readMobilePushNotificationOpenedMessage(message: unknown): OpenedPushNotification {
  const pushNotificationFields = readMobilePushNotificationOpenedMessageFields(message);
  return {
    notificationId: readStringOrNull(pushNotificationFields.notificationId),
    notificationType: readStringOrNull(pushNotificationFields.notificationType),
    redirectTo: readStringOrNull(pushNotificationFields.redirectTo),
    appState: readStringOrNull(pushNotificationFields.appState),
    platformOS: readStringOrNull(pushNotificationFields.platformOS),
    channel: "mobile",
  };
}

/**
 * The `deliveryId` of the mobile app's `push_notification_opened` message, or null when it has none. Every copy
 * the app sends of one tap carries the same `deliveryId`; the page confirms each copy with it.
 */
export function readMobilePushNotificationOpenedDeliveryId(message: unknown): string | null {
  const deliveryId = readMobilePushNotificationOpenedMessageFields(message).deliveryId;
  return typeof deliveryId === "string" && deliveryId !== "" ? deliveryId : null;
}

/** The desktop service worker's message to an open Jungle window, or null for any other message. */
export function readServiceWorkerPushNotificationOpenedMessage(messageData: unknown): OpenedPushNotification | null {
  if (!isPlainObject(messageData) || messageData.type !== PUSH_NOTIFICATION_OPENED_SERVICE_WORKER_MESSAGE_TYPE) {
    return null;
  }
  return {
    notificationId: readStringOrNull(messageData.notificationId),
    notificationType: readStringOrNull(messageData.notificationType),
    redirectTo: readStringOrNull(messageData.redirectTo),
    appState: "open_window",
    platformOS: "web",
    channel: "desktop",
  };
}

/**
 * Answers the desktop service worker's tap message, which comes with a port for the answer. The service worker
 * asks every open Jungle window at once and has the push opened in one of them only: the most recently used
 * window that answers in time. A page that does not listen for taps (an email action page, a marketing page)
 * never answers, and when no window answers the service worker opens a new one instead. So this window answers
 * at once and opens the push only when the service worker picks it. Any other message is left alone.
 */
export function answerServiceWorkerPushNotificationOpenedMessage(
  messageEvent: Pick<MessageEvent, "data" | "ports">,
  openThePushNotificationInThisWindow: (openedPushNotification: OpenedPushNotification) => void
): void {
  const openedPushNotification = readServiceWorkerPushNotificationOpenedMessage(messageEvent.data);
  const answerPort = messageEvent.ports?.[0];
  if (!openedPushNotification || !answerPort) return;
  answerPort.onmessage = (answerPortMessageEvent: MessageEvent) => {
    const serviceWorkerReply = answerPortMessageEvent.data;
    const isPickedToOpenThePush =
      isPlainObject(serviceWorkerReply) && serviceWorkerReply.type === PUSH_NOTIFICATION_OPEN_IN_THIS_WINDOW_MESSAGE_TYPE;
    if (!isPickedToOpenThePush) return;
    answerPort.close();
    openThePushNotificationInThisWindow(openedPushNotification);
  };
  answerPort.postMessage({ type: PUSH_NOTIFICATION_RECEIVED_BY_WINDOW_MESSAGE_TYPE });
}

/**
 * A window the desktop service worker opened for a tap (its address carries `push_notification_id` or
 * `push_notification_type`), or null for any other arrival. The screen the push is about is the address
 * without what the service worker added to it.
 */
export function readPushNotificationNewWindowArrival(pathname: string, search: string): OpenedPushNotification | null {
  const searchParams = new URLSearchParams(search);
  if (
    !searchParams.has(PUSH_NOTIFICATION_ID_URL_PARAMETER) &&
    !searchParams.has(PUSH_NOTIFICATION_TYPE_URL_PARAMETER)
  ) {
    return null;
  }
  const notificationId = searchParams.get(PUSH_NOTIFICATION_ID_URL_PARAMETER);
  const notificationType = searchParams.get(PUSH_NOTIFICATION_TYPE_URL_PARAMETER);
  searchParams.delete(PUSH_NOTIFICATION_ID_URL_PARAMETER);
  searchParams.delete(PUSH_NOTIFICATION_TYPE_URL_PARAMETER);
  if (searchParams.get("utm_source")?.startsWith(PUSH_NOTIFICATION_UTM_SOURCE_PREFIX)) {
    searchParams.delete("utm_source");
  }
  const remainingQuery = searchParams.toString();
  return {
    notificationId,
    notificationType,
    redirectTo: `${pathname.replace(/^\//, "")}${remainingQuery ? `?${remainingQuery}` : ""}`,
    appState: "new_window",
    platformOS: "web",
    channel: "desktop",
  };
}

/**
 * The address without the two parameters the service worker added for a tap (other parameters, such as its
 * `utm_source`, stay), or null when the address carries neither.
 */
export function removePushNotificationOpenParametersFromAddress(href: string): string | null {
  const address = new URL(href);
  if (
    !address.searchParams.has(PUSH_NOTIFICATION_ID_URL_PARAMETER) &&
    !address.searchParams.has(PUSH_NOTIFICATION_TYPE_URL_PARAMETER)
  ) {
    return null;
  }
  address.searchParams.delete(PUSH_NOTIFICATION_ID_URL_PARAMETER);
  address.searchParams.delete(PUSH_NOTIFICATION_TYPE_URL_PARAMETER);
  return `${address.pathname}${address.search}${address.hash}`;
}

let pendingPushNotificationNewWindowArrival: OpenedPushNotification | null = null;

/**
 * At app startup, before the router reads the address: remembers a window the service worker opened for a tap
 * until AppRouter can report it, and takes the two parameters out of the address (same history entry), so a
 * reload or a copied link does not count the tap again.
 */
export function rememberPushNotificationNewWindowArrivalAtAppStartup(): void {
  pendingPushNotificationNewWindowArrival = readPushNotificationNewWindowArrival(
    window.location.pathname,
    window.location.search
  );
  const addressWithoutPushNotificationParameters = removePushNotificationOpenParametersFromAddress(
    window.location.href
  );
  if (addressWithoutPushNotificationParameters !== null) {
    window.history.replaceState(window.history.state, "", addressWithoutPushNotificationParameters);
  }
}

/** The remembered arrival, handed over exactly once per page load. */
export function takePendingPushNotificationNewWindowArrival(): OpenedPushNotification | null {
  const pushNotificationNewWindowArrival = pendingPushNotificationNewWindowArrival;
  pendingPushNotificationNewWindowArrival = null;
  return pushNotificationNewWindowArrival;
}

// Characters the address parser drops anywhere ("\t", "\n", "\r") or reads as a slash ("\"), which would let
// "/\n/example.com" or "\example.com" become "//example.com": another site.
// eslint-disable-next-line no-control-regex -- control characters are exactly what this refuses
const CHARACTERS_NOT_ALLOWED_IN_A_REDIRECT = /[\u0000-\u001F\u007F\\]/;
const URL_SCHEME_AT_START = /^[A-Za-z][A-Za-z0-9+.-]*:/;

/**
 * The in-app path for a push's `redirectTo`, "/" followed by it (its query kept, so "?startReview=true" gives
 * "/?startReview=true"), or null when there is nowhere to go: empty, or not a safe relative path (a scheme, a
 * leading "//", a backslash or a control character). One leading slash is tolerated, so "/leaderboard" and
 * "leaderboard" are the same.
 */
export function getInAppPathForPushNotificationRedirect(redirectTo: string | null): string | null {
  if (typeof redirectTo !== "string") return null;
  const relativePath = redirectTo.startsWith("/") ? redirectTo.slice(1) : redirectTo;
  if (!relativePath) return null;
  if (relativePath.startsWith("/")) return null;
  if (CHARACTERS_NOT_ALLOWED_IN_A_REDIRECT.test(relativePath)) return null;
  if (URL_SCHEME_AT_START.test(relativePath)) return null;
  return `/${relativePath}`;
}

/**
 * Where a tap takes the learner from the page they are on. Today's review (home with `startReview`) is started
 * rather than opened as an address, and not for a learner who is already in a review; a screen the page is
 * already showing (AppRouter adds its own parameters to every address, so extra ones do not count) is left as
 * it is.
 */
export function decidePushNotificationDestination(
  redirectTo: string | null,
  currentPathname: string,
  currentSearch: string
): PushNotificationDestination {
  const inAppPath = getInAppPathForPushNotificationRedirect(redirectTo);
  if (!inAppPath) return { kind: "stay" };

  // The base only lets the parser split the path from the query; it is never used.
  const destination = new URL(inAppPath, "https://app.invalid");
  if (destination.pathname === "/" && destination.searchParams.get(START_TODAYS_REVIEW_URL_PARAMETER)) {
    return currentPathname.startsWith("/review-deck") ? { kind: "stay" } : { kind: "start_todays_review" };
  }

  const currentSearchParams = new URLSearchParams(currentSearch);
  const isPageAlreadyShowingTheDestination =
    destination.pathname === currentPathname &&
    Array.from(destination.searchParams.entries()).every(
      ([parameterName, parameterValue]) => currentSearchParams.get(parameterName) === parameterValue
    );
  return isPageAlreadyShowingTheDestination ? { kind: "stay" } : { kind: "open_in_app_path", inAppPath };
}
